import { isArray, isEmpty, isNil, isNumber, isObject, map, trim } from 'lodash';

/**
 * Helper function that takes in an object or array of objects and attempts to
 * truncate an array attribute (as indicated by `attributePath`) to be no longer
 * than `maxItems` elements.
 *
 * The `attributePath` may be a simple attribute name (e.g. "orders") or a path
 * to a nested attribute (e.g. "returns.items.line_items"). The `attributePath`
 * may not be empty. If the path does not exist in the data object, the unchanged
 * data is returned. For nested arrays, transformation is applied to each element.
 *
 * Error is thrown if a wrong data type is encountered while traversing the path
 * (for example, a scalar value where an array or an object would be expected).
 *
 * @param {Object|Object[]} dataObject
 * @param {String} attributePath
 * @param {Number} maxItems
 */
export function truncateArrayAttribute(dataObject, attributePath, maxItems) {
  const pathSegments = trim(attributePath)
    .split('.')
    .map(segment => trim(segment));

  const isInvalidPath = pathSegments.findIndex(segment => !segment) >= 0;
  const isInvalidData = isNil(dataObject) || !(isArray(dataObject) || isObject(dataObject));
  if (isInvalidPath || isInvalidData || !isNumber(maxItems) || maxItems <= 0) {
    throw Error('truncateArray: missing or invalid parameters');
  }

  return truncateAtPath(dataObject, '', pathSegments, maxItems);
}

function truncateAtPath(currentData, parentPath, remainingPathSegments, maxItems) {
  if (isEmpty(remainingPathSegments) || isNil(currentData)) return currentData;

  const currentSegment = remainingPathSegments[0];
  const currentPath = parentPath ? `${parentPath}.${currentSegment}` : currentSegment;

  if (isArray(currentData)) {
    return map(currentData, item => truncateAtPath(item, parentPath, remainingPathSegments, maxItems));
  } else if (isObject(currentData)) {
    const currentElement = currentData[currentSegment];
    if (remainingPathSegments.length <= 1) {
      // We at the end of the path. Truncate the element
      if (isNil(currentElement)) return currentData;
      if (isArray(currentElement)) {
        const truncated = Array.from(currentElement).slice(0, maxItems);
        return {
          ...currentData,
          [currentSegment]: truncated,
        };
      }
      throw Error(`Wrong data at path "${currentPath}". Expected an array, got ${typeof currentElement}`);
    } else {
      const truncated = truncateAtPath(currentElement, currentPath, remainingPathSegments.slice(1), maxItems);
      const ret = { ...currentData };
      if (truncated !== undefined) {
        ret[currentSegment] = truncated;
      }
      return ret;
    }
  } else {
    throw Error(`Wrong data at path "${currentPath}". Expected an object or array, got ${typeof currentData}`);
  }
}
